import {ChangeDetectionStrategy, Component, effect, inject, input, OnInit, output, signal} from '@angular/core';
import {AppConfigService} from '../../../../services/app-config.service';
import {SearchService} from '../../../../services/search/search.service';
import {
  SearchWidgetFiltersPageEnum,
  SearchWidgetFiltersPageLabelEnum,
} from '../../enums/search-widget-filters-page.enum';
import {QueryParams} from '../../../../types/QueryParams';
import {CpruTabsService} from '../../../../services/cpru-tabs.service';
import {CpruTab} from '../../../../types/CpruTab';
import {SearchWidgetV3StateService} from '../../services/search-widget-v3-state.service';
import {SearchWidgetDataItem} from '../../../../types/SearchWidgetDataItem';
import {SearchWidgetDataItemGroupedFlat} from '../../../../types/SearchWidgetDataItemGroupedFlat';
import {WidgetDataTypeEnum} from '../../../../enums/WidgetDataTypeEnum';

@Component({
  selector: 'app-search-categories-v3',
  templateUrl: './search-categories-v3.component.html',
  styleUrls: ['./search-categories-v3.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchCategoriesV3Component implements OnInit {
  public showBackButtonInline = input<boolean>(false);
  public cpruTabsService = inject(CpruTabsService);
  public tabs = this.cpruTabsService.tabs;
  public highlightedSection = this.cpruTabsService.highlightedSection;
  public currentPageUpdated = output<SearchWidgetFiltersPageEnum>();
  protected SearchWidgetFiltersPageEnum = SearchWidgetFiltersPageEnum;
  protected currentPage = signal<SearchWidgetFiltersPageEnum>(SearchWidgetFiltersPageEnum.MAIN);
  protected careServiceUnitGroups = signal<SearchWidgetDataItemGroupedFlat[]>([]);
  protected careServiceUnits = signal<SearchWidgetDataItem[]>([]);
  protected careServiceTypes = signal<SearchWidgetDataItem[]>([]);
  protected symptoms = signal<SearchWidgetDataItem[]>([]);
  protected selectedGroupIndex = signal<number>(-1);
  protected isLoading = signal<boolean>(false);
  private searchService = inject(SearchService);
  private widgetState = inject(SearchWidgetV3StateService);
  private loadedDataTypes = signal<Set<WidgetDataTypeEnum>>(new Set());
  private previousHighlightedSection: string = null;

  constructor(public appConfigService: AppConfigService) {
    this.currentPage.set(SearchWidgetFiltersPageEnum.MAIN);

    effect(
      () => {
        if (this.previousHighlightedSection !== this.highlightedSection()) {
          this.handleTabChange(this.highlightedSection());
        }
      },
      {allowSignalWrites: true},
    );

    effect(() => {
      this.currentPageUpdated.emit(this.currentPage());
    });
  }

  public ngOnInit(): void {
    this.updateTabs();
    this.cpruTabsService.setHighlightedSection(SearchWidgetFiltersPageEnum.CARE_SERVICE_UNIT_GROUPS);
  }

  public onTabChange(index: number): void {}

  public navigateBack(): void {
    if (this.currentPage() === SearchWidgetFiltersPageEnum.CARE_SERVICE_UNITS) {
      this.currentPage.set(SearchWidgetFiltersPageEnum.CARE_SERVICE_UNIT_GROUPS);
    } else if (this.currentPage() === SearchWidgetFiltersPageEnum.CARE_SERVICE_TYPES) {
      this.currentPage.set(SearchWidgetFiltersPageEnum.CARE_SERVICE_TYPE_GROUPS);
    } else if (this.currentPage() === SearchWidgetFiltersPageEnum.SYMPTOMS) {
      this.currentPage.set(SearchWidgetFiltersPageEnum.SYMPTOMS_GROUPS);
    }
  }

  public onItemClick(routerLink: string[], queryParams: QueryParams): void {
    this.closeThisPanel();
  }

  public selectCareServiceUnitGroup(index: number): void {
    this.selectedGroupIndex.set(index);
    const group = this.careServiceUnitGroups()[index];
    if (group && group.value) {
      this.careServiceUnits.set(group.value);
      this.currentPage.set(SearchWidgetFiltersPageEnum.CARE_SERVICE_UNITS);
    }
  }

  public onCareServiceUnitClick(routerLink: string[], queryParams: QueryParams): void {
    this.onItemClick(routerLink, queryParams);
  }

  public onCareServiceTypeClick(routerLink: string[], queryParams: QueryParams): void {
    this.onItemClick(routerLink, queryParams);
  }

  public onSymptomClick(routerLink: string[], queryParams: QueryParams): void {
    this.onItemClick(routerLink, queryParams);
  }

  private handleTabChange(tabName: string): void {
    this.previousHighlightedSection = tabName;
    switch (tabName) {
      case SearchWidgetFiltersPageEnum.CARE_SERVICE_UNIT_GROUPS:
        this.currentPage.set(SearchWidgetFiltersPageEnum.CARE_SERVICE_UNIT_GROUPS);
        break;
      case SearchWidgetFiltersPageEnum.CARE_SERVICE_TYPE_GROUPS:
        this.currentPage.set(SearchWidgetFiltersPageEnum.CARE_SERVICE_TYPE_GROUPS);
        break;
      case SearchWidgetFiltersPageEnum.SYMPTOMS_GROUPS:
        this.currentPage.set(SearchWidgetFiltersPageEnum.SYMPTOMS_GROUPS);
        break;
    }

    this.loadDataForCurrentTab();
  }

  private loadDataForCurrentTab(): void {
    const currentTab = this.highlightedSection();

    switch (currentTab) {
      case SearchWidgetFiltersPageEnum.CARE_SERVICE_UNIT_GROUPS:
        this.loadCaregiverUnitTypes();
        break;
      case SearchWidgetFiltersPageEnum.CARE_SERVICE_TYPE_GROUPS:
        this.loadCareServiceTypes();
        break;
      case SearchWidgetFiltersPageEnum.SYMPTOMS_GROUPS:
        this.loadSymptoms();
        break;
      default:
        console.log('No matching tab found for', currentTab);
    }
  }

  private loadCaregiverUnitTypes(): void {
    if (this.loadedDataTypes().has(WidgetDataTypeEnum.CAREGIVER_UNIT_TYPES)) {
      return;
    }

    this.isLoading.set(true);
    this.searchService.getWidgetData(WidgetDataTypeEnum.CAREGIVER_UNIT_TYPES).subscribe({
      next: (data) => {
        this.processCaregiverUnitTypes(data);
        const loadedTypes = new Set(this.loadedDataTypes());
        loadedTypes.add(WidgetDataTypeEnum.CAREGIVER_UNIT_TYPES);
        this.loadedDataTypes.set(loadedTypes);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading caregiver unit types', err);
        this.isLoading.set(false);
      },
    });
  }

  private loadCareServiceTypes(): void {
    if (this.loadedDataTypes().has(WidgetDataTypeEnum.CARE_SERVICE_TYPES)) {
      return;
    }

    this.isLoading.set(true);
    this.searchService.getWidgetData(WidgetDataTypeEnum.CARE_SERVICE_TYPES).subscribe({
      next: (data) => {
        this.processCareServiceTypes(data);
        const loadedTypes = new Set(this.loadedDataTypes());
        loadedTypes.add(WidgetDataTypeEnum.CARE_SERVICE_TYPES);
        this.loadedDataTypes.set(loadedTypes);
        this.isLoading.set(false);
      },
      error: (err) => {
        this.isLoading.set(false);
      },
    });
  }

  private loadSymptoms(): void {
    if (this.loadedDataTypes().has(WidgetDataTypeEnum.SYMPTOMS)) {
      return;
    }

    this.isLoading.set(true);
    this.searchService.getWidgetData(WidgetDataTypeEnum.SYMPTOMS).subscribe({
      next: (data) => {
        this.processSymptoms(data);
        const loadedTypes = new Set(this.loadedDataTypes());
        loadedTypes.add(WidgetDataTypeEnum.SYMPTOMS);
        this.loadedDataTypes.set(loadedTypes);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('Error loading symptoms', err);
        this.isLoading.set(false);
      },
    });
  }

  private closeThisPanel(): void {
    this.widgetState.updateMobileCategoriesPanelOpenValue(false);
    this.widgetState.updateDesktopCategoriesPanelOpenValue(false);
    this.widgetState.updateIsDesktopSuggestionsPanelOpen(true);
    this.widgetState.updateIsSearchWidgetExpanded(false);
  }

  private updateTabs(): void {
    const tabs: CpruTab[] = [];

    tabs.push({
      name: SearchWidgetFiltersPageEnum.CARE_SERVICE_UNIT_GROUPS,
      label: SearchWidgetFiltersPageLabelEnum.CARE_SERVICE_UNIT_GROUPS,
      mobileOnly: false,
      desktopOnly: false,
    });

    tabs.push({
      name: SearchWidgetFiltersPageEnum.CARE_SERVICE_TYPE_GROUPS,
      label: SearchWidgetFiltersPageLabelEnum.CARE_SERVICE_TYPE_GROUPS,
      mobileOnly: false,
      desktopOnly: false,
    });

    tabs.push({
      name: SearchWidgetFiltersPageEnum.SYMPTOMS_GROUPS,
      label: SearchWidgetFiltersPageLabelEnum.SYMPTOMS_GROUPS,
      mobileOnly: false,
      desktopOnly: false,
    });

    this.cpruTabsService.updateTabs(tabs);
  }

  private processCaregiverUnitTypes(data: SearchWidgetDataItem[]): void {
    if (!data || data.length === 0) {
      return;
    }

    const dataWithLinks = this.searchService.updateWidgetDataWithRouterLinks({
      caregiverUnitTypes: data,
      caregiverUnitTypesGrouped: {},
      careServiceTypes: [],
      careServiceTypesGrouped: {},
      symptoms: [],
      symptomsGrouped: {},
      popularCaregiverUnitTypes: [],
      popularCareServiceTypes: [],
      bookingTypes: [],
    }).caregiverUnitTypes;

    const groupedData: {[key: string]: SearchWidgetDataItem[]} = {};

    dataWithLinks.forEach((item) => {
      const key = item.area?.name || 'Other';
      if (!groupedData[key]) {
        groupedData[key] = [];
      }
      groupedData[key].push(item);
    });

    const groups: SearchWidgetDataItemGroupedFlat[] = Object.keys(groupedData)
      .sort()
      .map((key) => ({
        key,
        value: groupedData[key].sort((a, b) => a.name.localeCompare(b.name)),
      }));

    this.careServiceUnitGroups.set(groups);
  }

  private processCareServiceTypes(data: SearchWidgetDataItem[]): void {
    if (!data || data.length === 0) {
      return;
    }

    const dataWithLinks = this.searchService.updateWidgetDataWithRouterLinks({
      caregiverUnitTypes: [],
      caregiverUnitTypesGrouped: {},
      careServiceTypes: data,
      careServiceTypesGrouped: {},
      symptoms: [],
      symptomsGrouped: {},
      popularCaregiverUnitTypes: [],
      popularCareServiceTypes: [],
      bookingTypes: [],
    }).careServiceTypes;
    const sortedData = [...dataWithLinks].sort((a, b) => a.name.localeCompare(b.name));
    this.careServiceTypes.set(sortedData);
  }

  private processSymptoms(data: SearchWidgetDataItem[]): void {
    if (!data || data.length === 0) {
      return;
    }

    const dataWithLinks = this.searchService.updateWidgetDataWithRouterLinks({
      caregiverUnitTypes: [],
      caregiverUnitTypesGrouped: {},
      careServiceTypes: [],
      careServiceTypesGrouped: {},
      symptoms: data,
      symptomsGrouped: {},
      popularCaregiverUnitTypes: [],
      popularCareServiceTypes: [],
      bookingTypes: [],
    }).symptoms;
    const sortedData = [...dataWithLinks].sort((a, b) => a.name.localeCompare(b.name));
    this.symptoms.set(sortedData);
  }
}
